import { useEffect, useRef } from 'react';

type Piece = { x: number; y: number; vx: number; vy: number; w: number; h: number; rot: number; vr: number; color: string };

const TOKENS = ['--rr-brand', '--rr-success', '--rr-warning', '--rr-info', '--rr-red'];
// One burst at a time: when two celebrations land together (the readiness page and the helper both
// noticing a newly complete profile), the second one within 4 seconds is skipped.
let lastBurst = { at: 0, owner: {} as object };

const FALLBACK = ['#1DBE8E', '#0F766E', '#D97706', '#1D4ED8', '#DC2626'];

/**
 * A short burst of confetti drawn on a canvas over the page (no library). Decorative only: aria-hidden,
 * never catches clicks, and removes itself after `durationMs`. Callers must not render it when the
 * visitor prefers reduced motion (show a static message instead).
 */
export default function Confetti({ durationMs = 2800, pieces = 140, onDone }: { durationMs?: number; pieces?: number; onDone?: () => void }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const ownerRef = useRef<object>({});

  useEffect(() => {
    if (lastBurst.owner !== ownerRef.current && Date.now() - lastBurst.at < 4000) {
      doneRef.current?.();
      return;
    }
    lastBurst = { at: Date.now(), owner: ownerRef.current };
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      doneRef.current?.();
      return;
    }
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const style = getComputedStyle(document.documentElement);
    const colors = TOKENS.map((t, i) => style.getPropertyValue(t).trim() || FALLBACK[i]);
    const W = window.innerWidth;
    const list: Piece[] = Array.from({ length: pieces }, (_, i) => ({
      x: W / 2 + (Math.random() - 0.5) * W * 0.5,
      y: -20 - Math.random() * 120,
      vx: (Math.random() - 0.5) * 7,
      vy: 2 + Math.random() * 4,
      w: 6 + Math.random() * 6,
      h: 8 + Math.random() * 8,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: colors[i % colors.length],
    }));

    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = now - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const fade = t > durationMs - 600 ? Math.max(0, (durationMs - t) / 600) : 1;
      ctx.globalAlpha = fade;
      for (const p of list) {
        p.vy += 0.08;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (t < durationMs) frame = requestAnimationFrame(tick);
      else doneRef.current?.();
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, [durationMs, pieces]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      data-testid="readiness-confetti"
      style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 60 }}
    />
  );
}
